import { StandardEvents } from '@shopify/events';

/**
 * BESOCKS · Selector de talla rápido en la tarjeta de producto.
 * Sustituye el modal de "quick add" por un panel inline: talla -> añadir.
 */
const I18N = {
  es: { added: 'Añadido', error: 'Sin stock' },
  en: { added: 'Added', error: 'Out of stock' },
  fr: { added: 'Ajouté', error: 'Rupture de stock' },
  de: { added: 'Hinzugefügt', error: 'Nicht auf Lager' },
  it: { added: 'Aggiunto', error: 'Esaurito' },
};
const LANG = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();
const TXT = I18N[LANG] || I18N.en;

class BesocksQuickSize extends HTMLElement {
  #closeTimer;

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-bq-toggle]');
    this.panel = this.querySelector('[data-bq-panel]');
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('input[name="id"]');
    this.addBtn = this.querySelector('[data-bq-add]');
    this.addLabel = this.addBtn?.querySelector('[data-bq-add-label]');
    this.defaultLabel = this.addLabel?.textContent ?? '';

    this.toggleBtn?.addEventListener('click', this.#onToggle);
    this.panel?.addEventListener('click', this.#onPanelClick);
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onOutsideClick);
    clearTimeout(this.#closeTimer);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(focusFirst = false) {
    document.querySelectorAll('besocks-quick-size[open]').forEach((el) => el !== this && el.close());
    this.setAttribute('open', '');
    this.panel.hidden = false;
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    this.closest('.quick-add')?.setAttribute('data-bq-open', '');
    if (focusFirst) this.panel.querySelector('.bq__size:not([disabled])')?.focus({ preventScroll: true });
  }

  close() {
    this.removeAttribute('open');
    this.panel.hidden = true;
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    this.closest('.quick-add')?.removeAttribute('data-bq-open');
    this.#reset();
  }

  #reset() {
    this.panel.querySelectorAll('.bq__size[aria-checked="true"]').forEach((b) => b.setAttribute('aria-checked', 'false'));
    if (this.idInput) this.idInput.value = '';
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.removeAttribute('data-state');
    }
    if (this.addLabel) this.addLabel.textContent = this.defaultLabel;
  }

  #onToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Solo mover el foco si se abrió con teclado (event.detail === 0)
    this.isOpen ? this.close() : this.open(event.detail === 0);
  };

  #onPanelClick = (event) => {
    // Evita que el clic en el panel navegue a la ficha del producto
    event.stopPropagation();

    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-bq-close]')) {
      event.preventDefault();
      this.close();
      this.toggleBtn?.focus({ preventScroll: true });
      return;
    }

    const size = target.closest('.bq__size');
    if (size && !size.disabled) {
      event.preventDefault();
      this.panel.querySelectorAll('.bq__size').forEach((b) => b.setAttribute('aria-checked', String(b === size)));
      this.idInput.value = size.dataset.variantId;
      this.addBtn.disabled = false;
      this.addBtn.removeAttribute('data-state');
      this.addLabel.textContent = this.defaultLabel;
    }
  };

  #onCartUpdate = (event) => {
    if (!this.addBtn || !this.panel.contains(/** @type {Node} */ (event.target))) return;
    this.addBtn.dataset.state = 'loading';
    this.addLabel.textContent = this.dataset.loadingText || '…';
    event.promise
      ?.then((result) => (result?.detail?.didError ? this.#onError() : this.#onSuccess()))
      .catch(() => this.#onError());
  };

  #onSuccess() {
    this.addBtn.dataset.state = 'added';
    this.addLabel.textContent = this.dataset.addedText || TXT.added;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.close(), 1400);
  }

  #onError() {
    this.addBtn.dataset.state = 'error';
    this.addLabel.textContent = this.dataset.errorText || TXT.error;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.addBtn.removeAttribute('data-state');
      this.addLabel.textContent = this.defaultLabel;
    }, 2500);
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  #onOutsideClick = (event) => {
    if (this.isOpen && !this.contains(/** @type {Node} */ (event.target))) this.close();
  };
}

if (!customElements.get('besocks-quick-size')) {
  customElements.define('besocks-quick-size', BesocksQuickSize);
}
